import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, MapPin, Phone, Mail, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactInfoManager = () => {
  const [contactInfo, setContactInfo] = useState({
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/contact-info`);
      setContactInfo({
        address: response.data.address || '',
        city: response.data.city || '',
        country: response.data.country || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        facebook_url: response.data.facebook_url || '',
        twitter_url: response.data.twitter_url || '',
        linkedin_url: response.data.linkedin_url || ''
      });
    } catch (error) {
      console.error('Error fetching contact info:', error);
      toast.error('Failed to load contact information');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setContactInfo({
      ...contactInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/contact-info`, contactInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact information updated successfully');
      fetchContactInfo();
    } catch (error) {
      console.error('Error updating contact info:', error);
      toast.error('Failed to update contact information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Contact Information</h2>
        <p className="text-gray-600">Manage your association's contact details</p>
      </div>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Edit Contact Details</CardTitle>
          <CardDescription>
            Update the contact information displayed on your website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} data-testid="contact-info-form" className="space-y-6">
            {/* Address Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-orange-600 font-semibold">
                <MapPin size={20} />
                <span>Address Information</span>
              </div>
              
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  name="address"
                  data-testid="input-address"
                  value={contactInfo.address}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="e.g., PEA PNG Headquarters, Waigani Drive"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    data-testid="input-city"
                    value={contactInfo.city}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="e.g., Port Moresby"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    name="country"
                    data-testid="input-country"
                    value={contactInfo.country}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="e.g., Papua New Guinea"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-blue-600 font-semibold">
                <Phone size={20} />
                <span>Contact Details</span>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    value={contactInfo.phone}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="e.g., +675 123 4567"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="input-email"
                    value={contactInfo.email}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="e.g., info@peapng.org"
                  />
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-green-600 font-semibold">
                <Globe size={20} />
                <span>Social Media Links (Optional)</span>
              </div>

              <div>
                <Label htmlFor="facebook_url">Facebook URL</Label>
                <Input
                  id="facebook_url"
                  name="facebook_url"
                  data-testid="input-facebook"
                  value={contactInfo.facebook_url}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="https://facebook.com/peapng"
                />
              </div>

              <div>
                <Label htmlFor="twitter_url">Twitter URL</Label>
                <Input
                  id="twitter_url"
                  name="twitter_url"
                  data-testid="input-twitter"
                  value={contactInfo.twitter_url}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="https://twitter.com/peapng"
                />
              </div>

              <div>
                <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                <Input
                  id="linkedin_url"
                  name="linkedin_url"
                  data-testid="input-linkedin"
                  value={contactInfo.linkedin_url}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="https://linkedin.com/company/peapng"
                />
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                data-testid="save-contact-info-btn"
                disabled={saving}
                className="w-full md:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                <Save className="mr-2" size={20} />
                {saving ? 'Saving...' : 'Save Contact Information'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactInfoManager;
